import { Dispatch } from 'redux';
import SalesForceController from '../controllers/SalesForceController';
import commonHandler from '../errorHandlers/common-handler';
import { OauthUrlResponse } from '../model/SalesForceModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class SalesForceCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveAccounts: string;
  retrieveAttributes;
  retrievedAvailableTags: string;
  revokeAction: string;
  resetStore: string;
  tagsDisabledAction: string;

  constructor() {
    super('SALESFORCE');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveAttributes = this.makeActionKey('RETRIEVED_ATTRIBUTES');
    this.retrievedAvailableTags = this.makeActionKey(
      'RETRIEVED_AVAILABLE_TAGS',
    );
    this.tagsDisabledAction = this.makeActionKey('TAGS_DISABLED_ERROR');
    this.revokeAction = this.makeActionKey('REVOKE');
    this.resetStore = this.makeActionKey('RESET_STORE');
  }

  getSalesForceOauthUrl(tokenId?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse =
          await SalesForceController.getOauthUrl(tokenId);
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await SalesForceController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        return commonHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  revokeAccess(accountId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAction));

      try {
        await SalesForceController.revokeAccess(accountId);
        dispatch(this.unbusy());
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      }
    };
  }

  getAttributes(facebookPageId: number, tokenId: number, force?: boolean) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAttributes));

      try {
        const response = await SalesForceController.getAttributes(
          facebookPageId,
          tokenId,
          force,
        );
        dispatch({
          type: this.retrieveAttributes,
          attributes: response,
        });
        return true;
      } catch (error) {
        commonHandler(dispatch)(error);
        return false;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getAvailableTags(facebookPageId: number, tokenId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrievedAvailableTags));

      try {
        const { tags } = await SalesForceController.getAvailableTags(
          facebookPageId,
          tokenId,
        );
        dispatch({
          type: this.retrievedAvailableTags,
          tags,
        });
        dispatch(this.unbusy());
        return true;
      } catch (error) {
        dispatch(this.unbusy());

        if (await this.tagsDisabledError(error)) {
          dispatch({ type: this.tagsDisabledAction });
          return false;
        }
        commonHandler(dispatch)(error);
        return false;
      }
    };
  }

  invalidateStore() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.resetStore });
    };
  }

  private async tagsDisabledError(response) {
    if (response?.status !== 422) return false;

    const body = await response.json();
    if (
      body.message.includes('INVALID_TYPE:') &&
      body.message.includes('not supported')
    ) {
      return true;
    }

    return false;
  }
}

const salesForceCRUDActions = new SalesForceCRUDActions();

export default salesForceCRUDActions;

export const useSalesForceCRUDActions = getActionHookBuilder(
  salesForceCRUDActions,
  [
    'getSalesForceOauthUrl',
    'getAccounts',
    'getAttributes',
    'getAvailableTags',
    'revokeAccess',
    'invalidateStore',
  ],
);
